
import { Allotment } from 'allotment'
import React, { SyntheticEvent, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { closeRightSidebar, openRightSidebar } from '@/entities/userInterface/slices/userInterfaceSlice'
import { syncWithMosaicState } from '@/entities/widgetWindows/thunks/syncWithMosaicState'
import { MosaicLayout } from '@/fragments/MosaicLayout/MosaicLayout'
import { MosaicLayoutContext, useMosaicLayoutActions } from '@/fragments/MosaicLayout/MosaicLayoutContext'
import { MosaicLayoutSkeleton } from '@/fragments/MosaicLayout/MosaicLayoutSkeleton'
import { OperationsSecondaryControls } from '@/fragments/OperationsSecondaryControls/OperationsSecondaryControls'
import { OrderDetailsSidebar } from '@/fragments/OrderDetailsSidebar/OrderDetailsSidebar'
import { OrderDetailsSidebarCollapse } from '@/fragments/OrderDetailsSidebar/OrderDetailsSidebarCollapse'
import { WidgetToolbar } from '@/fragments/WidgetToolbar'
import { useWidgetActions } from '@/fragments/WidgetToolbar/lib/useWidgetActions'
import { WidgetToolbarContext } from '@/fragments/WidgetToolbar/WidgetToolbarContext'
import { WidgetToolbarSettings } from '@/fragments/WidgetToolbar/WidgetToolbarSettings'
import { WidgetToolbarSkeleton } from '@/fragments/WidgetToolbar/WidgetToolbarSkeleton'
import { useTour } from '@/shared/components/DriverTour/libs/useTour'
import { getLeaves } from '@/shared/components/Mosaic'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faTruckFast } from '@fortawesome/pro-solid-svg-icons'
import { Box, Divider, Grid2 as Grid, useTheme } from '@mui/material'

import { useOperationWindowLayout } from './libs/useOperationWindowLayout'
import { PlanningTreeView } from './PlanningTreeView'
import { StyledLeftDrawer, StyledTab, StyledTabPanel, StyledTabs, StyledTitle } from './StyledOperationsPage'
import { setupPlanning } from './thunks/setupPlanning'
import { getOperationWidgetElementMapping } from './WidgetElementMapping'

export type PlanningPageProps = {
  //
}

export const PlanningPage: React.FC<PlanningPageProps> = () => {
  const activeGroupId = useAppSelector(state => state.userInterface.activeGroupId)
  const isRightSidebarOpen = useAppSelector(state => state.userInterface.isRightSidebarOpen)
  const { isLoading, isSuccess } = useAppSelector(state => state.planningPage)
  const { mosaicRootNode, setRootNode, widgets, setWidgets } = useOperationWindowLayout(activeGroupId, 'planning')
  const { moveWidgetToToolbar, moveWidgetToWindow, moveWidgetAroundToolbar, hideWidget, unHideWidget } = useWidgetActions(widgets, setWidgets)
  const { replaceWidgetWindow, closeWidgetWindows, spawnWidgetInTopRightCorner } = useMosaicLayoutActions(mosaicRootNode, setRootNode)
  const [isWidgetSettingsOpen, setWidgetSettingsOpen] = React.useState(false)
  const widgetElementMapping = useMemo(() => getOperationWidgetElementMapping('planning'), [])

  const dispatch = useAppDispatch()
  const theme = useTheme()
  const navigate = useNavigate()

  // Setup planning page
  useEffect(() => {
    dispatch(setupPlanning())
  }, [activeGroupId, dispatch])

  // Sync redux store with the current mosaic layout
  useEffect(() => {
    dispatch(syncWithMosaicState({ mosaicNodes: getLeaves(mosaicRootNode) }))
  }, [activeGroupId, dispatch, mosaicRootNode])

  // Show tour of the planning page if not completed
  useTour('planning', !isLoading && isSuccess)

  const onTabChange = (_: SyntheticEvent<Element, Event>, newValue: number) => {
    navigate(newValue === 0 ? '/operations/planning' : '/operations/runsheet')
  }

  const onExpandEditOrderSidebar = () => {
    dispatch(openRightSidebar())
  }

  const onCollapseEditOrderSidebar = () => {
    dispatch(closeRightSidebar())
  }

  return (
    <MosaicLayoutContext.Provider value={{ replaceWidgetWindow, closeWidgetWindows, spawnWidgetInTopRightCorner }}>
      <WidgetToolbarContext.Provider
        value={{
          widgets: widgets,
          isWidgetSettingsOpen,
          setWidgetSettingsOpen,
          moveWidgetToToolbar,
          moveWidgetToWindow,
          moveWidgetAroundToolbar,
          hideWidget,
          unHideWidget,
        }}
      >
        <Allotment>
          <Allotment.Pane preferredSize={theme.layout.defaultLeftDrawerWidth} minSize={230} maxSize={320}>
            <StyledLeftDrawer>
              <StyledTitle variant="h1">Operations</StyledTitle>
              <StyledTabs value={0} onChange={onTabChange} aria-label="Switch between planning and runsheet">
                <StyledTab id="tab-planning-nav" disableRipple label="Planning" />
                <StyledTab id="tab-runsheet-nav" disableRipple label="Runsheet" />
              </StyledTabs>
              <Divider />
              <StyledTabPanel
                id="planning-tree-view"
                role="tabpanel"
              >
                <PlanningTreeView />
              </StyledTabPanel>
            </StyledLeftDrawer>
          </Allotment.Pane>
          <Allotment.Pane>
            <div id="planning-main" style={{ width: '100%', height: '100%', backgroundColor: theme.palette.background.default }}>
              {isLoading ? (
                <Box sx={{ paddingTop: pxToRem(4) }}>
                  <WidgetToolbarSkeleton />
                  <MosaicLayoutSkeleton />
                </Box>
              ) : (
                <>
                  <Grid container direction="row" justifyContent="flex-start" alignItems="center" wrap={'nowrap'} sx={{ paddingTop: pxToRem(4) }}>
                    <Grid id="planning-widget-dock" flexGrow={1}>
                      <WidgetToolbar
                        widgets={widgets}
                        setWidgets={setWidgets}
                      />
                    </Grid>
                    <Grid>
                      <OperationsSecondaryControls />
                    </Grid>
                  </Grid>
                  <Box sx={{ height: 'calc(100% - 64px)' }}>
                    <Allotment>
                      <Allotment.Pane preferredSize={theme.layout.defaultLeftDrawerWidth}>
                        <MosaicLayout
                          id="planning-workspace"
                          currentNode={mosaicRootNode}
                          setCurrentNode={(currentNode) => {
                            setRootNode(currentNode)
                            // moveToNextStep()
                          }}
                          widgetElementMapping={widgetElementMapping}
                          zeroStateProps={{
                            icon: faTruckFast,
                          }}
                        />
                      </Allotment.Pane>
                      <Allotment.Pane
                        preferredSize={isRightSidebarOpen ? 400 : 30}
                        minSize={isRightSidebarOpen ? 320 : 30}
                        maxSize={isRightSidebarOpen ? 600 : 30}
                      >
                        {isRightSidebarOpen ? (
                          <OrderDetailsSidebar onCollapse={onCollapseEditOrderSidebar} />
                        ) : (
                          <OrderDetailsSidebarCollapse onClick={onExpandEditOrderSidebar} />
                        )}
                      </Allotment.Pane>
                    </Allotment>
                  </Box>
                </>
              )}
            </div>
          </Allotment.Pane>
          <Allotment.Pane minSize={300} maxSize={300} visible={isWidgetSettingsOpen}>
            <WidgetToolbarSettings />
          </Allotment.Pane>
        </Allotment>
      </WidgetToolbarContext.Provider>
    </MosaicLayoutContext.Provider>
  )
}
